import type { AxiosResponse } from "axios";
import * as Device from "expo-device";
import { Redirect, router, useGlobalSearchParams } from "expo-router";
import { useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { PasswordInput } from "~/components/form/PasswordInput";
import { GuestFieldGroup } from "~/components/layout/guest/GuestFieldGroup";
import { Page } from "~/components/layout/Page";
import { BaseText, Title } from "~/components/layout/text";
import { Flex, Gap, Padding } from "~/constants/layout";
import type { ApiError } from "~/providers/ApiProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { type TokenResponse, useSession } from "~/providers/SessionProvider";
import { empty, filled } from "~/util";
import { createClient } from "~/util/api";

export default function Share() {
    const { token }: { token: string } = useGlobalSearchParams();
    const { setShareToken } = useSession();
    const { loading, setLoading } = useFeedback();
    const errorBag = useErrorBag<"email" | "password">();
    const [password, setPassword] = useState<string>("");

    if (empty(token)) {
        return <Redirect href="/start" />;
    }

    const device_name = Device.deviceName ?? Device.modelName ?? `${Device.osName} ${Device.osVersion}`;
    const canSubmit = !loading && filled(password) && !errorBag.hasAny(["email", "password"]);

    const attemptLogin = async () => {
        if (empty(password)) {
            errorBag.add("password", "Password must not be blank");
            return;
        }

        if (!canSubmit) {
            return;
        }

        setLoading(true);

        await createClient()
            .post("/share/login", {
                email: token,
                password,
                device_name,
            })
            .then((response: AxiosResponse<TokenResponse>) => {
                setShareToken(response.data.token);
                setTimeout(() => router.navigate("/dashboard"), 10);
            })
            .catch((error: ApiError) => {
                if (!error.response?.data?.errors) {
                    errorBag.add("email", error.response?.data?.message ?? "Could log access shared account.");
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Page gradient spacer>
            <View style={[Flex.flex, Padding.bottom_xxl]}>
                <GuestFieldGroup>
                    <View style={[Gap.sm]}>
                        <Title size="xl">View my QENDO data</Title>
                        <BaseText>Sign in to access my shared QENDO app data</BaseText>
                    </View>
                    <PasswordInput
                        name="password"
                        autoComplete="password"
                        autoCapitalize={"none"}
                        value={password}
                        onChangeText={setPassword}
                        disabled={loading}
                        onFocus={() => errorBag.forget(["email"])}
                    />
                    <Button
                        title="Sign in"
                        variant="primary"
                        size="lg"
                        onPress={attemptLogin}
                        loading={loading}
                        disabled={!canSubmit}
                    />
                </GuestFieldGroup>
            </View>
        </Page>
    );
}
